import { supabase } from '../supabaseClient';

// Aceptación de invitaciones en el servidor (RPC accept_invitation, migración
// 20260930020000). El navegador solo manda el token: el rol, la empresa y el
// correo salen de la invitación y del JWT de la sesión.

export type AcceptStatus = 'aceptada' | 'ya_aceptada' | 'ya_miembro';

export interface AcceptOk {
  ok: true;
  status: AcceptStatus;
  companyId: string;
  companyName: string | null;
  role: string;
}

export interface AcceptError {
  ok: false;
  code: string;
  message: string;
}

export type AcceptResult = AcceptOk | AcceptError;

// Token de una invitación en curso, para el alta con confirmación de correo:
// el enlace de confirmación puede abrirse en otra pestaña, así que se guarda
// en localStorage (sessionStorage es por pestaña). Caduca con la invitación.
const STORAGE_KEY = 'ss_pending_invite_token';
const LEGACY_SESSION_KEY = 'pending_invite';
export const INVITE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const ERROR_MESSAGES: Record<string, string> = {
  sin_sesion: 'Tu sesión expiró. Vuelve a abrir el enlace de la invitación.',
  sin_correo: 'No pudimos leer el correo de tu sesión. Cierra sesión y vuelve a abrir el enlace.',
  invitacion_no_encontrada: 'Esta invitación no existe. Pide una nueva al administrador de tu empresa.',
  correo_no_coincide: 'Esta invitación es para otro correo. Inicia sesión con el correo al que llegó la invitación.',
  invitacion_ya_usada: 'Esta invitación ya fue utilizada por otra cuenta. Pide una nueva al administrador.',
  invitacion_vencida: 'Esta invitación ha expirado. Solicita una nueva al administrador.',
  invitacion_no_vigente: 'Esta invitación ya no está vigente. Solicita una nueva al administrador.',
  rol_invalido: 'La invitación no es válida. Pide una nueva al administrador.',
  perfil_en_otra_empresa: 'Tu cuenta ya pertenece a otra empresa en StorSafe. Para unirte a esta, escríbenos a info@storsafe.app.',
};

const GENERIC_ERROR = 'No pudimos aceptar la invitación. Intenta de nuevo en unos minutos o contacta al administrador de tu empresa.';

export function inviteErrorFromMessage(raw: string | undefined | null): AcceptError {
  const text = raw ?? '';
  const code = Object.keys(ERROR_MESSAGES).find((c) => text.includes(c)) ?? 'desconocido';
  return { ok: false, code, message: ERROR_MESSAGES[code] ?? GENERIC_ERROR };
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function safeLocal(): StorageLike | null {
  try { return window.localStorage; } catch { return null; }
}

function safeSession(): StorageLike | null {
  try { return window.sessionStorage; } catch { return null; }
}

export function rememberInviteToken(token: string, now: number = Date.now(), storage: StorageLike | null = safeLocal()): void {
  try { storage?.setItem(STORAGE_KEY, JSON.stringify({ token, savedAt: now })); } catch { /* sin almacenamiento */ }
}

export function forgetInviteToken(storage: StorageLike | null = safeLocal()): void {
  try { storage?.removeItem(STORAGE_KEY); } catch { /* sin almacenamiento */ }
}

// Lee y borra el token pendiente (se intenta una sola vez). También recoge el
// formato anterior (sessionStorage 'pending_invite') de pestañas abiertas
// antes del despliegue.
export function takeInviteToken(
  now: number = Date.now(),
  storage: StorageLike | null = safeLocal(),
  legacy: StorageLike | null = safeSession(),
): string | null {
  let token: string | null = null;
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    storage?.removeItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { token?: unknown; savedAt?: unknown };
      if (typeof parsed.token === 'string' && parsed.token
          && typeof parsed.savedAt === 'number' && now - parsed.savedAt <= INVITE_MAX_AGE_MS) {
        token = parsed.token;
      }
    }
  } catch { /* valor corrupto: se descarta */ }
  try {
    const rawLegacy = legacy?.getItem(LEGACY_SESSION_KEY);
    legacy?.removeItem(LEGACY_SESSION_KEY);
    if (!token && rawLegacy) {
      const parsed = JSON.parse(rawLegacy) as { token?: unknown };
      if (typeof parsed.token === 'string' && parsed.token) token = parsed.token;
    }
  } catch { /* valor corrupto: se descarta */ }
  return token;
}

export async function acceptInvitation(token: string): Promise<AcceptResult> {
  const { data, error } = await supabase.rpc('accept_invitation', { p_token: token });
  if (error) return inviteErrorFromMessage(error.message);

  const row = (data ?? {}) as { status?: string; company_id?: string; company_name?: string | null; role?: string };
  if (row.status !== 'aceptada' && row.status !== 'ya_aceptada' && row.status !== 'ya_miembro') {
    return inviteErrorFromMessage(null);
  }

  forgetInviteToken();

  // Aviso al admin solo la primera vez (si otra pestaña ya la aceptó, no se
  // repite). El servidor saca empresa, correo, rol y destinatario de la
  // sesión y de la base (A5): no se manda ningún dato.
  if (row.status === 'aceptada' && row.company_id) {
    const { error: mailError } = await supabase.functions.invoke('send-email', {
      body: { type: 'invitation_accepted' },
    });
    if (mailError) console.error('[invitaciones] aviso al admin falló:', mailError);
  }

  return {
    ok: true,
    status: row.status,
    companyId: row.company_id ?? '',
    companyName: row.company_name ?? null,
    role: row.role ?? '',
  };
}
